import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import {
  GeofenceHoldTimeModel,
  useDeleteGeofenceHoldTimeMutation,
  useListGeofenceHoldTimesQuery,
} from '@casops/store/apis/geofenceHoldTimesApi';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import i18n from '@casops/i18n';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';
import { EmptyCell } from '@casops/components/tables/formatters/genericFormatters';

type Model = GeofenceHoldTimeModel;

const modelName = ModelName.GeofenceHoldTime;
const sidePanel = SidePanel.ManageGeofenceHoldTime;
const permission = Permission.GeofenceManagement;

const canDeleteItem = (data: Model): boolean => !data.used;

const getIsGreaterTrans = (isGreater: boolean): string =>
  i18n.t(isGreater ? 'GreaterThan' : 'LessThan');

const getItemName = (data: Model): string => {
  return `${getIsGreaterTrans(data.isGreater)} ${data.time}`;
};

const isGreaterValueGetter = ({ data }: ValueGetterParams<Model, boolean>): string => {
  return typeof data?.isGreater !== 'undefined' ? getIsGreaterTrans(data.isGreater) : EmptyCell;
};

const cellRendererParams: ModelActionsCellRendererParams<Model> = {
  modelName,
  permission,
  editProps: {
    sidePanel,
    canEditItem: true,
  },
  deleteProps: {
    canDeleteItem,
    getQueryArg: getDefaultModelArg,
    useDeleteModelMutation: useDeleteGeofenceHoldTimeMutation,
    getItemName,
  },
};

export function GeofenceHoldTimes() {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      {
        colId: 'IsGreater',
        headerName: t('Fields.IsGreater'),
        valueGetter: isGreaterValueGetter,
        minWidth: 100,
        maxWidth: 250,
      },
      { field: 'time', headerName: t('Fields.Time'), minWidth: 100, maxWidth: 200 },
      { field: 'desc', headerName: t('Fields.Description'), minWidth: 300 },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListGeofenceHoldTimesQuery(undefined, {
    refetchOnMountOrArgChange: 120,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  return (
    <CollectionView<Model>
      sliceKey={modelName}
      modelName={modelName}
      columnDefs={columnDefs}
      rowData={data}
      hasData={hasData}
      noDataDescription={noDataDescription}
      actions={
        <CreateItemButton modelName={modelName} sidePanel={sidePanel} permission={permission} />
      }
    />
  );
}

export default GeofenceHoldTimes;
